// React
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { useIsDrawerOpen } from '@react-navigation/drawer';

// Redux
import { connect } from 'react-redux';
import { clearState } from '_redux/rootActions';
import { getFriendlyUsername } from '_redux/user/utils';
import { getIsViewLocked, getIsSettingViewLock, getUserCognitoSub } from '_redux/user/selectors';
import { setViewLock } from '_redux/user/actions';

import Modal from '_components/common/Modal.component';
import UserProfileModalContent from '_components/user/UserProfileModalContent.component';

// Amplify
import { Auth } from 'aws-amplify';

// React Native
import { Image, TouchableOpacity } from 'react-native';

// Native Base Components
import {
  Button,
  Spinner,
  Text,
  View,
} from 'native-base';

// Style
import { styles } from '_components/common/AppHeader.style';
import { styles as global } from '_style/Global.style';

class AppHeader extends Component {

  constructor(props) {
    super(props);

    this.state = {
      username: '',
      showUserProfileModal: false,
      showUnlockConfirmModal: false,
      userProfileData: {},
      showOfflineView: false
    };

    this._getUsername = this._getUsername.bind(this);
    this._signOut = this._signOut.bind(this);

    this._hideUserProfileModal = this._hideUserProfileModal.bind(this);
    this._showUserProfileModal = this._showUserProfileModal.bind(this);

    this._toggleDrawer = this._toggleDrawer.bind(this);
    this._onLockPress = this._onLockPress.bind(this);
  }

  /**
   * Executed when the component mounts. Get the username from Auth
   * to show in the header.
   */
  async componentDidMount() {
    this._getUsername();
  }

  componentWillUnmount() {
    clearTimeout(this.offlineTimeout); // Clear timeout on unmount
  }


  /**
   * Gets the username out of the Auth and adds it to the state.
   */
  async _getUsername() {
    const userInfo = await Auth.currentUserInfo();
    const { attributes, username } = userInfo;

    let displayName = getFriendlyUsername({ ...userInfo.attributes, username });

    this.setState({
      username: displayName,
      userProfileData: {
        firstName: attributes.given_name,
        lastName: attributes.family_name,
        email: attributes.email,
        phoneNumber: attributes.phone_number,
      }
    });
  }

  async _showUserProfileModal() {
    this.setState({
      showUserProfileModal: true,
    });
  }
  _hideUserProfileModal() {
    this.setState({
      showUserProfileModal: false,
    });
    this._getUsername();
  }

  async _toggleDrawer() {
    this.props.toggleDrawer();
  }

  _onLockPress() {
    const { isViewLocked, cognitoSub, setViewLock } = this.props;
    if (isViewLocked) {
      this.setState({ showUnlockConfirmModal: true });
    } else {
      setViewLock(cognitoSub, true);
    }
  }

  /**
   * Sign the user out and navigate back to the login screen.
   */
  _signOut() {
    const { clearState } = this.props;

    Auth.signOut({ global: true })
      .then(() => {
        clearState();
      });
  }

  render() {
    const {
      showUserProfileModal,
      showUnlockConfirmModal,
      userProfileData,
    } = this.state;

    const {
      jobName,
      disconnectError,
      isViewLocked,
      isSettingViewLock,
    } = this.props;


    // Check if both jobName and disconnectError are truthy
    if (jobName && disconnectError && !this.state.showOfflineView) {
      // Use setTimeout to delay the rendering of the view after 1 second
      setTimeout(() => {
        // Set a state to indicate that the view should be shown
        this.setState({ showOfflineView: true });
      }, 1000);
    } else if (!disconnectError && this.state.showOfflineView) {
      this.setState({ showOfflineView: false });
    }

    return (
      <View style={[styles.main]}>
        <View style={[styles.appHeader]}>
          <View style={styles.leftHeader}>
            <View style={styles.drawerMenuButton}>
              <Button transparent onPress={this._toggleDrawer}>
                <Image
                  resizeMode='contain'
                  style={{ height: 32, width: 32 }}
                  source={require('_assets/images/drawer-menu-button.png')}
                />
              </Button>
            </View>
            <Image
              resizeMode='contain'
              style={{ height: 36, width: 245 }}
              source={require('_assets/images/app-header-logo.png')}
            />
            {this.state.showOfflineView && (
              <View style={{ flexDirection: 'row' }}>
                <Image
                  resizeMode='contain'
                  style={{ height: 30, width: 30, marginLeft: 10 }} // Adjusted width to match the height of the image
                  source={require('_assets/images/outline-cloud-off-white-24dp.png')}
                />
                <Text style={[styles.usernameText]}> Offline</Text>
              </View>
            )}
          </View>
          <View>
            <Text style={[styles.jobNameText]}>
              {jobName}
            </Text>
          </View>
          <View style={styles.rightHeader}>
            {this.props.currentRouteName === 'Dashboard' && (
              <TouchableOpacity
                disabled={isSettingViewLock}
                onPress={this._onLockPress}
                style={{
                  backgroundColor: isViewLocked ? '#C0392B' : '#27AE60',
                  borderRadius: 8,
                  paddingHorizontal: 10,
                  paddingVertical: 6,
                  marginRight: 10,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <View style={{ height: 24, width: 24, alignItems: 'center', justifyContent: 'center' }}>
                  {isSettingViewLock
                    ? <Spinner color='white' size='small' style={{ height: 24, width: 24 }} />
                    : <Image
                        style={{ height: 24, width: 24 }}
                        source={isViewLocked
                          ? require('_assets/images/lock-icon-2.svg')
                          : require('_assets/images/unlock-icon-2.svg')
                        }
                      />
                  }
                </View>
              </TouchableOpacity>
            )}
            <Button transparent onPress={this._showUserProfileModal}>
              <Text style={[styles.usernameText]}>
                {this.state.username}
              </Text>
              <Image
                style={{ height: 32, width: 32 }}
                source={require('_assets/images/user-icon.png')}
              />
            </Button>
          </View>

        </View>
        {/* User Profile Modal */}
        <Modal
          isVisible={showUserProfileModal}
          title={'USER PROFILE SETTINGS'}
          content={!showUserProfileModal ? null :
            <UserProfileModalContent
              formData={userProfileData}
            />
          }
          onClose={this._hideUserProfileModal}
        />

        <Modal
          isVisible={showUnlockConfirmModal}
          title={'UNLOCK'}
          hideExitIcon={true}
          content={
            <View style={{ padding: 24, alignItems: 'center' }}>
              <Text style={{ color: '#FAFAFA', fontSize: 16, marginBottom: 24, textAlign: 'center' }}>
                Do you want to unlock?
              </Text>
              <View style={{ flexDirection: 'row', gap: 12 }}>
                <TouchableOpacity
                  style={[global.cancelButton, { paddingHorizontal: 24, paddingVertical: 10 }]}
                  onPress={() => this.setState({ showUnlockConfirmModal: false })}
                >
                  <Text style={[global.cancelButtonText]}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[global.confirmationButton, { paddingHorizontal: 24, paddingVertical: 10 }]}
                  onPress={() => {
                    const { cognitoSub, setViewLock } = this.props;
                    this.setState({ showUnlockConfirmModal: false });
                    setViewLock(cognitoSub, false);
                  }}
                >
                  <Text style={[global.confirmationButtonText]}>Unlock</Text>
                </TouchableOpacity>
              </View>
            </View>
          }
          onClose={() => this.setState({ showUnlockConfirmModal: false })}
        />
      </View>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    jobName: state.dashboard.lastViewedJobName,
    disconnectError: state.iot.disconnectError,
    isViewLocked: getIsViewLocked(state),
    isSettingViewLock: getIsSettingViewLock(state),
    cognitoSub: getUserCognitoSub(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    clearState: () => dispatch(clearState()),
    setViewLock: (cognitoSub, isViewLocked) => dispatch(setViewLock(cognitoSub, isViewLocked)),
  };
};

AppHeader.propTypes = {
  clearState: PropTypes.func.isRequired,
  // jobName: PropTypes.string.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(AppHeader);


